import type { ComponentConfig } from "@puckeditor/core"
import { SectionBase, sectionStyle } from "../lib/section"
import { getResolvers, SiteProduct } from "../lib/resolvers"
import { ProductTile } from "../lib/product-tile"

export type ProductShowcaseProps = SectionBase & {
  title: string
  subtitle: string
  /** Medusa collection handle; empty = all products */
  collectionHandle: string
  /** Comma separated tags; empty = ignore */
  tags: string
  limit: number
  columns: 2 | 3 | 4
  ctaLabel: string
  /**
   * Resolved by the storefront renderer through the registered resolvers
   * (resolveData below). The editor shows placeholders.
   */
  _products?: SiteProduct[]
}

export const ProductShowcase: ComponentConfig<ProductShowcaseProps> = {
  fields: {
    title: { type: "textarea", contentEditable: true },
    subtitle: { type: "textarea", contentEditable: true },
    collectionHandle: { type: "text", label: "Collection handle", contentEditable: true },
    tags: { type: "text", label: "Tags (comma separated)", contentEditable: true },
    limit: { type: "number", min: 1, max: 24 },
    columns: {
      type: "radio",
      options: [
        { value: 2, label: "2 columns" },
        { value: 3, label: "3 columns" },
        { value: 4, label: "4 columns" },
      ],
    },
    ctaLabel: { type: "text", label: "Card button label", contentEditable: true },
    background: {
      type: "radio",
      options: [
        { value: "none", label: "Transparent" },
        { value: "muted", label: "Muted" },
        { value: "accent", label: "Dark" },
      ],
    },
    paddingY: {
      type: "radio",
      options: [
        { value: "none", label: "None" },
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ],
    },
  },

  resolveData: async ({ props }) => {
    const resolvers = getResolvers()
    if (!resolvers) {
      // Admin editor or unregistered host: keep placeholders.
      return { props: {} }
    }
    const products = await resolvers.showcaseProducts({
      collectionHandle: props.collectionHandle,
      tags: props.tags,
      limit: props.limit,
    })
    return { props: { _products: products } }
  },

  defaultProps: {
    title: "Featured products",
    subtitle: "",
    collectionHandle: "",
    tags: "",
    limit: 8,
    columns: 4,
    ctaLabel: "Add to cart",
    background: "none",
    paddingY: "lg",
  },

  render: ({
    title,
    subtitle,
    limit,
    columns,
    ctaLabel,
    background,
    paddingY,
    _products,
  }) => {
    const s = sectionStyle({ background, paddingY })
    const products = _products ?? []
    const hasData = products.length > 0
    const tiles = hasData ? products : Array.from({ length: Math.min(limit, columns * 2) })

    return (
      <section style={s}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
          {(title || subtitle) && (
            <div style={{ textAlign: "center", marginBottom: 40 }}>
              <h2 style={{ fontSize: "clamp(1.75rem,4vw,2.5rem)", fontWeight: 800, marginBottom: 12 }}>{title}</h2>
              {subtitle && <p style={{ fontSize: "1.125rem", color: "#6b7280" }}>{subtitle}</p>}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 24 }}>
            {tiles.map((p, i) =>
              hasData ? (
                <ProductTile key={(p as SiteProduct).id} product={p as SiteProduct} ctaLabel={ctaLabel} />
              ) : (
                <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
                  <div style={{ aspectRatio: "1/1", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 13 }}>
                    Product
                  </div>
                  <div style={{ padding: 16 }}>
                    <div style={{ height: 14, width: "70%", background: "#e5e7eb", borderRadius: 4, marginBottom: 8 }} />
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ height: 14, width: 60, background: "#e5e7eb", borderRadius: 4 }} />
                      <button
                        style={{
                          padding: "6px 12px",
                          background: "var(--site-accent, #7c3aed)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {ctaLabel}
                      </button>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </section>
    )
  },
}
